import {
  Box,
  Button,
  Flex,
  Text,
  useToast,
  Tabs,
  TabList,
  TabPanel,
  TabPanels,
  Tab,
  Spinner,
  Center,
  Badge,
  VStack,
  HStack,
  Divider,
  IconButton,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  useDisclosure,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
} from "@chakra-ui/react";
import { ChevronLeftIcon, RepeatIcon } from "@chakra-ui/icons";
import { useEffect, useState, useRef } from "react";
import { useHistory, useLocation } from "react-router-dom";
import {
  getVotingPeriodById,
  getVotingPeriodProjects,
  getVotingPeriodVotes,
  pauseCampaign,
  resumeCampaign,
  setWinner,
  removeEligibleProject,
  calculateEligibleProjects,
  VotingPeriod,
  VotingPeriodProject,
  Vote,
} from "utils/apiService/PinkSale";
import ProjectVotesModal from "./ProjectVotesModal";
import UpdateCampaignTimesModal from "components/modal/UpdateCampaignTimesModal";
import AddProjectModal from "components/modal/AddProjectModal";
import PreviewEligibleProjectTab from "./PreviewEligibleProjectTab";

// Format date
const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleString();
};

// Get chain name
const getChainName = (chainId: number) => {
  const chainMap: { [key: number]: string } = {
    1: "Ethereum",
    56: "BSC",
    501424: "Solana",
  };
  return chainMap[chainId] || `Chain ${chainId}`;
};

// Format currency
const formatCurrency = (value: number) => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

// Extract voting period ID from URL
function getVotingPeriodIdFromUrl(url: string) {
  const regex = /\/admin\/pinksale-voting-period\/([a-zA-Z0-9]+)/;
  const match = url.match(regex);
  return match ? match[1] : null;
}

const VotingPeriodDetail = () => {
  const history = useHistory();
  const location = useLocation();
  const toast = useToast();
  const {
    isOpen: isJsonModalOpen,
    onOpen: onJsonModalOpen,
    onClose: onJsonModalClose,
  } = useDisclosure();
  const {
    isOpen: isVotingPeriodJsonModalOpen,
    onOpen: onVotingPeriodJsonModalOpen,
    onClose: onVotingPeriodJsonModalClose,
  } = useDisclosure();
  const {
    isOpen: isVotesModalOpen,
    onOpen: onVotesModalOpen,
    onClose: onVotesModalClose,
  } = useDisclosure();
  const {
    isOpen: isUpdateTimesModalOpen,
    onOpen: onUpdateTimesModalOpen,
    onClose: onUpdateTimesModalClose,
  } = useDisclosure();
  const {
    isOpen: isPauseDialogOpen,
    onOpen: onPauseDialogOpen,
    onClose: onPauseDialogClose,
  } = useDisclosure();
  const {
    isOpen: isResumeDialogOpen,
    onOpen: onResumeDialogOpen,
    onClose: onResumeDialogClose,
  } = useDisclosure();
  const {
    isOpen: isAddProjectModalOpen,
    onOpen: onAddProjectModalOpen,
    onClose: onAddProjectModalClose,
  } = useDisclosure();
  const cancelRef = useRef<HTMLButtonElement>(null);
  const [isPausing, setIsPausing] = useState(false);
  const [isResuming, setIsResuming] = useState(false);
  const {
    isOpen: isSetWinnerDialogOpen,
    onOpen: onSetWinnerDialogOpen,
    onClose: onSetWinnerDialogClose,
  } = useDisclosure();
  const {
    isOpen: isDeleteProjectDialogOpen,
    onOpen: onDeleteProjectDialogOpen,
    onClose: onDeleteProjectDialogClose,
  } = useDisclosure();
  const {
    isOpen: isCalculateEligibleDialogOpen,
    onOpen: onCalculateEligibleDialogOpen,
    onClose: onCalculateEligibleDialogClose,
  } = useDisclosure();
  const [selectedProjectForWinner, setSelectedProjectForWinner] =
    useState<VotingPeriodProject | null>(null);
  const [selectedProjectForDelete, setSelectedProjectForDelete] =
    useState<VotingPeriodProject | null>(null);
  const [isSettingWinner, setIsSettingWinner] = useState(false);
  const [isDeletingProject, setIsDeletingProject] = useState(false);
  const [isCalculatingEligible, setIsCalculatingEligible] = useState(false);

  // Extract ID from URL directly (similar to baby-token-detail)
  const id = getVotingPeriodIdFromUrl(
    location.pathname || window.location.href
  );

  const [votingPeriod, setVotingPeriod] = useState<VotingPeriod | null>(null);
  const [projects, setProjects] = useState<VotingPeriodProject[]>([]);
  const [votes, setVotes] = useState<Vote[]>([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [votesLoading, setVotesLoading] = useState(false);
  const [selectedProject, setSelectedProject] =
    useState<VotingPeriodProject | null>(null);

  useEffect(() => {
    const fetchVotingPeriod = async () => {
      const currentId = getVotingPeriodIdFromUrl(
        location.pathname || window.location.href
      );
      if (!currentId) {
        setInitialLoading(false);
        return;
      }

      try {
        setInitialLoading(true);
        const data = await getVotingPeriodById(currentId);
        setVotingPeriod(data);
      } catch (error: any) {
        toast({
          title: "Error",
          description:
            error?.message || "Failed to fetch voting period details",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setInitialLoading(false);
      }
    };

    fetchVotingPeriod();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  useEffect(() => {
    const fetchProjects = async () => {
      const currentId = getVotingPeriodIdFromUrl(
        location.pathname || window.location.href
      );
      if (!currentId) {
        setProjectsLoading(false);
        return;
      }

      try {
        setProjectsLoading(true);
        const projectsData = await getVotingPeriodProjects(currentId);
        setProjects(projectsData);
      } catch (error: any) {
        toast({
          title: "Error",
          description: error?.message || "Failed to fetch projects",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setProjectsLoading(false);
      }
    };

    fetchProjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  useEffect(() => {
    const fetchVotes = async () => {
      const currentId = getVotingPeriodIdFromUrl(
        location.pathname || window.location.href
      );
      if (!currentId) {
        setVotesLoading(false);
        return;
      }

      try {
        setVotesLoading(true);
        const votesData = await getVotingPeriodVotes(currentId);
        setVotes(votesData);
      } catch (error: any) {
        toast({
          title: "Error",
          description: error?.message || "Failed to fetch votes",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setVotesLoading(false);
      }
    };

    fetchVotes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const getStatusBadge = (period: VotingPeriod) => {
    if (period.isCompleted) {
      return <Badge colorScheme="green">Completed</Badge>;
    }
    if (period.isPaused) {
      return <Badge colorScheme="yellow">Paused</Badge>;
    }
    const now = new Date();
    const startTime = new Date(period.startTime);
    const endTime = new Date(period.endTime);

    if (now < startTime) {
      return <Badge colorScheme="blue">Upcoming</Badge>;
    }
    if (now >= startTime && now <= endTime) {
      return <Badge colorScheme="green">Active</Badge>;
    }
    return <Badge colorScheme="gray">Ended</Badge>;
  };

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    toast({
      title: "Copied",
      description: "Copied to clipboard",
      status: "success",
      duration: 2000,
      isClosable: true,
    });
  };

  const handleViewProjectJson = (project: VotingPeriodProject) => {
    setSelectedProject(project);
    onJsonModalOpen();
  };

  const handleViewProjectVotes = (project: VotingPeriodProject) => {
    setSelectedProject(project);
    onVotesModalOpen();
  };

  const handleReload = async () => {
    const currentId = getVotingPeriodIdFromUrl(
      location.pathname || window.location.href
    );
    if (!currentId) return;

    // Reload voting period details only
    try {
      setLoading(true);
      const data = await getVotingPeriodById(currentId);
      setVotingPeriod(data);
      toast({
        title: "Success",
        description: "Voting period data refreshed",
        status: "success",
        duration: 2000,
        isClosable: true,
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description:
          error?.message || "Failed to refresh voting period details",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePauseCampaign = async () => {
    if (!votingPeriod) return;

    try {
      setIsPausing(true);
      const response = await pauseCampaign(votingPeriod._id);
      setVotingPeriod(response.campaign);
      toast({
        title: "Success",
        description: "Campaign paused successfully",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      onPauseDialogClose();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error?.message || "Failed to pause campaign",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsPausing(false);
    }
  };

  const handleResumeCampaign = async () => {
    if (!votingPeriod) return;

    try {
      setIsResuming(true);
      const response = await resumeCampaign(votingPeriod._id);
      setVotingPeriod(response.campaign);
      toast({
        title: "Success",
        description: "Campaign resumed successfully",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      onResumeDialogClose();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error?.message || "Failed to resume campaign",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsResuming(false);
    }
  };

  const handleSetWinnerClick = (project: VotingPeriodProject) => {
    setSelectedProjectForWinner(project);
    onSetWinnerDialogOpen();
  };

  const handleSetWinner = async () => {
    if (!votingPeriod || !selectedProjectForWinner) return;

    try {
      setIsSettingWinner(true);
      const response = await setWinner(votingPeriod._id, {
        launchpadId: selectedProjectForWinner.launchpadId,
      });
      setVotingPeriod(response.campaign);
      toast({
        title: "Success",
        description: "Winner set successfully",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      onSetWinnerDialogClose();
      setSelectedProjectForWinner(null);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error?.message || "Failed to set winner",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSettingWinner(false);
    }
  };

  const handleDeleteProjectClick = (project: VotingPeriodProject) => {
    setSelectedProjectForDelete(project);
    onDeleteProjectDialogOpen();
  };

  const handleDeleteProject = async () => {
    if (!votingPeriod || !selectedProjectForDelete) return;

    try {
      setIsDeletingProject(true);
      await removeEligibleProject(
        selectedProjectForDelete.campaignId,
        selectedProjectForDelete.launchpadId
      );

      // Refresh projects list after successful deletion
      const currentId = getVotingPeriodIdFromUrl(
        location.pathname || window.location.href
      );
      if (currentId) {
        const projectsData = await getVotingPeriodProjects(currentId);
        setProjects(projectsData);
      }

      toast({
        title: "Success",
        description: "Project removed successfully",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      onDeleteProjectDialogClose();
      setSelectedProjectForDelete(null);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error?.message || "Failed to remove project",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsDeletingProject(false);
    }
  };

  const handleCalculateEligible = async () => {
    if (!votingPeriod) return;

    try {
      setIsCalculatingEligible(true);
      const response = await calculateEligibleProjects(votingPeriod._id);

      // Refresh projects list and voting period data
      const currentId = getVotingPeriodIdFromUrl(
        location.pathname || window.location.href
      );
      if (currentId) {
        const [projectsData, votingPeriodData] = await Promise.all([
          getVotingPeriodProjects(currentId),
          getVotingPeriodById(currentId),
        ]);
        setProjects(projectsData);
        setVotingPeriod(votingPeriodData);
      }

      toast({
        title: "Success",
        description: `Calculated ${response.eligibleProjectsCount} eligible projects`,
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      onCalculateEligibleDialogClose();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error?.message || "Failed to calculate eligible projects",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsCalculatingEligible(false);
    }
  };

  if (initialLoading) {
    return (
      <Center py={20}>
        <Spinner size="xl" />
      </Center>
    );
  }

  if (!votingPeriod) {
    return (
      <Center py={20}>
        <Text>Voting period not found</Text>
      </Center>
    );
  }

  return (
    <Box pt={{ base: "130px", md: "80px", xl: "80px" }}>
      {/* Header with back button */}
      <Flex mb={6} align="center" gap={4}>
        <IconButton
          aria-label="Go back"
          icon={<ChevronLeftIcon />}
          onClick={() => history.goBack()}
          variant="ghost"
        />
        <VStack align="start" spacing={1}>
          <Text fontSize="2xl" fontWeight="bold">
            Voting Period Details
          </Text>
          <Text fontSize="sm" color="gray.500">
            Week {votingPeriod.weekNumber}, {votingPeriod.year}
          </Text>
        </VStack>
      </Flex>

      {/* Voting Period Info Card */}
      <Box p={6} bg="white" borderRadius="md" boxShadow="sm" mb={6}>
        {loading ? (
          <Center py={10}>
            <Spinner size="lg" />
          </Center>
        ) : (
          <VStack align="stretch" spacing={4}>
            <Flex justify="space-between" align="center">
              <Text fontSize="xl" fontWeight="bold">
                Information
              </Text>
              <HStack spacing={3}>
                {getStatusBadge(votingPeriod)}
                <Button
                  size="sm"
                  colorScheme="teal"
                  onClick={onCalculateEligibleDialogOpen}
                  isLoading={isCalculatingEligible}
                >
                  Calculate Eligible
                </Button>
                <Button
                  size="sm"
                  colorScheme="green"
                  onClick={onVotingPeriodJsonModalOpen}
                >
                  View Raw
                </Button>
                <IconButton
                  aria-label="Reload voting period details"
                  icon={<RepeatIcon />}
                  onClick={handleReload}
                  isLoading={loading}
                  size="sm"
                  colorScheme="blue"
                  variant="ghost"
                />
              </HStack>
            </Flex>
            <Divider />
            <HStack spacing={8} flexWrap="wrap">
              <Box>
                <Text fontSize="xs" color="gray.500">
                  Voting Period ID
                </Text>
                <Text
                  fontSize="sm"
                  fontFamily="mono"
                  cursor="copy"
                  onClick={() => handleCopy(votingPeriod._id)}
                  _hover={{ color: "blue.500", textDecoration: "underline" }}
                >
                  {votingPeriod._id.slice(0, 12)}...
                </Text>
              </Box>
              <Box>
                <Text fontSize="xs" color="gray.500">
                  Week Number
                </Text>
                <Text fontSize="sm">{votingPeriod.weekNumber}</Text>
              </Box>
              <Box>
                <Text fontSize="xs" color="gray.500">
                  Year
                </Text>
                <Text fontSize="sm">{votingPeriod.year}</Text>
              </Box>
              <Box>
                <Text fontSize="xs" color="gray.500">
                  Start Time
                </Text>
                <Text fontSize="sm">{formatDate(votingPeriod.startTime)}</Text>
              </Box>
              <Box>
                <Text fontSize="xs" color="gray.500">
                  End Time
                </Text>
                <Text fontSize="sm">{formatDate(votingPeriod.endTime)}</Text>
              </Box>
            </HStack>
            <Divider />
            <HStack spacing={8} flexWrap="wrap">
              <Box>
                <Text fontSize="xs" color="gray.500">
                  Eligibility Start Time
                </Text>
                <Text fontSize="sm">
                  {formatDate(votingPeriod.eligibilityStartTime)}
                </Text>
              </Box>
              <Box>
                <Text fontSize="xs" color="gray.500">
                  Eligibility End Time
                </Text>
                <Text fontSize="sm">
                  {formatDate(votingPeriod.eligibilityEndTime)}
                </Text>
              </Box>
              {votingPeriod.stats && (
                <>
                  <Box>
                    <Text fontSize="xs" color="gray.500">
                      Total Eligible Projects
                    </Text>
                    <Text fontSize="sm" fontWeight="bold">
                      {votingPeriod.stats.totalEligibleProjects}
                    </Text>
                  </Box>
                  <Box>
                    <Text fontSize="xs" color="gray.500">
                      Total Votes
                    </Text>
                    <Text fontSize="sm" fontWeight="bold">
                      {votingPeriod.stats.totalVotes}
                    </Text>
                  </Box>
                  <Box>
                    <Text fontSize="xs" color="gray.500">
                      Total Voting Power
                    </Text>
                    <Text fontSize="sm" fontWeight="bold">
                      {votingPeriod.stats.totalVotingPower.toLocaleString()}
                    </Text>
                  </Box>
                  <Box>
                    <Text fontSize="xs" color="gray.500">
                      Unique Voters
                    </Text>
                    <Text fontSize="sm" fontWeight="bold">
                      {votingPeriod.stats.uniqueVotersCount}
                    </Text>
                  </Box>
                </>
              )}
            </HStack>
            <Divider />
            <Flex justify="flex-end" gap={3}>
              {!votingPeriod.isPaused && !votingPeriod.isCompleted && (
                <Button colorScheme="yellow" onClick={onPauseDialogOpen}>
                  Pause Campaign
                </Button>
              )}
              {votingPeriod.isPaused && !votingPeriod.isCompleted && (
                <Button colorScheme="green" onClick={onResumeDialogOpen}>
                  Resume Campaign
                </Button>
              )}
              <Button colorScheme="purple" onClick={onAddProjectModalOpen}>
                Add eligible project
              </Button>
              <Button colorScheme="blue" onClick={onUpdateTimesModalOpen}>
                Update Times
              </Button>
            </Flex>
          </VStack>
        )}
      </Box>

      {/* Tabs */}
      <Tabs variant="enclosed">
        <TabList>
          <Tab>Projects</Tab>
          <Tab>Votes</Tab>
          <Tab>PreviewEligibleProject</Tab>
        </TabList>

        <TabPanels>
          {/* Projects Tab */}
          <TabPanel>
            <Flex justify="space-between" align="center" mb={4}>
              <Text fontSize="lg" fontWeight="semibold">
                Projects ({projects.length})
              </Text>
              <IconButton
                aria-label="Reload projects"
                icon={<RepeatIcon />}
                onClick={async () => {
                  const currentId = getVotingPeriodIdFromUrl(
                    location.pathname || window.location.href
                  );
                  if (!currentId) return;

                  try {
                    setProjectsLoading(true);
                    const projectsData = await getVotingPeriodProjects(
                      currentId
                    );
                    setProjects(projectsData);
                    toast({
                      title: "Success",
                      description: "Projects refreshed",
                      status: "success",
                      duration: 2000,
                      isClosable: true,
                    });
                  } catch (error: any) {
                    toast({
                      title: "Error",
                      description:
                        error?.message || "Failed to refresh projects",
                      status: "error",
                      duration: 5000,
                      isClosable: true,
                    });
                  } finally {
                    setProjectsLoading(false);
                  }
                }}
                isLoading={projectsLoading}
                size="sm"
                colorScheme="blue"
                variant="ghost"
              />
            </Flex>
            {projectsLoading ? (
              <Center py={10}>
                <Spinner size="xl" />
              </Center>
            ) : projects.length === 0 ? (
              <Center py={10}>
                <Text>No projects found</Text>
              </Center>
            ) : (
              <Box overflowX="auto">
                <Table
                  variant="simple"
                  size="sm"
                  bg="white"
                  borderRadius="md"
                  boxShadow="sm"
                >
                  <Thead>
                    <Tr>
                      <Th>Token Name</Th>
                      <Th>Symbol</Th>
                      <Th>Chain</Th>
                      <Th>Pool Address</Th>
                      <Th>Total Raised (USD)</Th>
                      <Th>Contributor Count</Th>
                      <Th>Voting Power</Th>
                      <Th>Status</Th>
                      <Th>Actions</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {projects.map((project) => (
                      <Tr key={project._id}>
                        <Td fontWeight="medium">
                          {project.launchpad?.token?.name || "N/A"}
                        </Td>
                        <Td>
                          <Badge colorScheme="blue">
                            {project.launchpad?.token?.symbol || "N/A"}
                          </Badge>
                        </Td>
                        <Td>{getChainName(project.chainId)}</Td>
                        <Td>
                          <Text
                            fontSize="xs"
                            fontFamily="mono"
                            cursor="copy"
                            _hover={{ color: "blue.500" }}
                            onClick={() => handleCopy(project.poolAddress)}
                          >
                            {project.poolAddress.slice(0, 10)}...
                          </Text>
                        </Td>
                        <Td>
                          {project.launchpad?.metadata?.finalizeTotalRaisedUsd
                            ? formatCurrency(
                                project.launchpad.metadata
                                  .finalizeTotalRaisedUsd
                              )
                            : "N/A"}
                        </Td>
                        <Td>
                          {project.launchpad?.pool?.contributorCount !== null &&
                          project.launchpad?.pool?.contributorCount !==
                            undefined
                            ? project.launchpad.pool.contributorCount.toLocaleString()
                            : "N/A"}
                        </Td>
                        <Td>{project.votingPower.toLocaleString()}</Td>
                        <Td>
                          <Badge
                            colorScheme={
                              project.status === "AUTO_ELIGIBLE"
                                ? "green"
                                : "gray"
                            }
                          >
                            {project.status}
                          </Badge>
                        </Td>
                        <Td>
                          <HStack spacing={2}>
                            <Button
                              size="xs"
                              colorScheme="blue"
                              onClick={() => handleViewProjectVotes(project)}
                            >
                              Votes
                            </Button>
                            <Button
                              size="xs"
                              colorScheme="green"
                              onClick={() => handleViewProjectJson(project)}
                            >
                              Raw
                            </Button>
                            <Button
                              size="xs"
                              colorScheme="purple"
                              onClick={() => handleSetWinnerClick(project)}
                            >
                              Set winner
                            </Button>
                            <Button
                              size="xs"
                              colorScheme="red"
                              onClick={() => handleDeleteProjectClick(project)}
                            >
                              Delete
                            </Button>
                          </HStack>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>
            )}
          </TabPanel>

          {/* Votes Tab */}
          <TabPanel>
            <Flex justify="space-between" align="center" mb={4}>
              <Text fontSize="lg" fontWeight="semibold">
                Votes ({votes.length})
              </Text>
              <IconButton
                aria-label="Reload votes"
                icon={<RepeatIcon />}
                onClick={async () => {
                  const currentId = getVotingPeriodIdFromUrl(
                    location.pathname || window.location.href
                  );
                  if (!currentId) return;

                  try {
                    setVotesLoading(true);
                    const votesData = await getVotingPeriodVotes(currentId);
                    setVotes(votesData);
                    toast({
                      title: "Success",
                      description: "Votes refreshed",
                      status: "success",
                      duration: 2000,
                      isClosable: true,
                    });
                  } catch (error: any) {
                    toast({
                      title: "Error",
                      description: error?.message || "Failed to refresh votes",
                      status: "error",
                      duration: 5000,
                      isClosable: true,
                    });
                  } finally {
                    setVotesLoading(false);
                  }
                }}
                isLoading={votesLoading}
                size="sm"
                colorScheme="blue"
                variant="ghost"
              />
            </Flex>
            {votesLoading ? (
              <Center py={10}>
                <Spinner size="xl" />
              </Center>
            ) : (
              <Box overflowX="auto">
                <Table
                  variant="simple"
                  size="sm"
                  bg="white"
                  borderRadius="md"
                  boxShadow="sm"
                >
                  <Thead>
                    <Tr>
                      <Th>Voter Address</Th>
                      <Th>Project ID</Th>
                      <Th>Campaign ID</Th>
                      <Th>Chain</Th>
                      <Th>Voting Power</Th>
                      <Th>Created At</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {votes.length === 0 ? (
                      <Tr>
                        <Td colSpan={6} textAlign="center" py={10}>
                          <Text color="gray.500">No votes found</Text>
                        </Td>
                      </Tr>
                    ) : (
                      votes.map((vote) => (
                        <Tr key={vote._id}>
                          <Td>
                            <Text
                              fontSize="xs"
                              fontFamily="mono"
                              cursor="copy"
                              _hover={{ color: "blue.500" }}
                              onClick={() => handleCopy(vote.voterAddress)}
                            >
                              {vote.voterAddress.slice(0, 10)}...
                              {vote.voterAddress.slice(-8)}
                            </Text>
                          </Td>
                          <Td>
                            <Text
                              fontSize="xs"
                              fontFamily="mono"
                              cursor="copy"
                              _hover={{ color: "blue.500" }}
                              onClick={() => handleCopy(vote.projectId)}
                            >
                              {vote.projectId.slice(0, 10)}...
                            </Text>
                          </Td>
                          <Td>
                            <Text
                              fontSize="xs"
                              fontFamily="mono"
                              cursor="copy"
                              _hover={{ color: "blue.500" }}
                              onClick={() => handleCopy(vote.campaignId)}
                            >
                              {vote.campaignId.slice(0, 10)}...
                            </Text>
                          </Td>
                          <Td>{getChainName(vote.chainId)}</Td>
                          <Td>{vote.votingPower.toLocaleString()}</Td>
                          <Td fontSize="xs">{formatDate(vote.createdAt)}</Td>
                        </Tr>
                      ))
                    )}
                  </Tbody>
                </Table>
              </Box>
            )}
          </TabPanel>

          {/* Preview Eligible Project Tab */}
          <TabPanel>
            <PreviewEligibleProjectTab votingPeriod={votingPeriod} />
          </TabPanel>
        </TabPanels>
      </Tabs>

      {/* Voting Period JSON Modal */}
      <Modal
        isOpen={isVotingPeriodJsonModalOpen}
        onClose={onVotingPeriodJsonModalClose}
        size="4xl"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            Voting Period JSON Data - Week {votingPeriod?.weekNumber},{" "}
            {votingPeriod?.year}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody fontSize="sm">
            <Box
              p={4}
              bg="gray.50"
              borderRadius="md"
              maxH="70vh"
              overflowY="auto"
            >
              <pre
                style={{
                  whiteSpace: "pre-wrap",
                  wordWrap: "break-word",
                  fontFamily: "monospace",
                  fontSize: "12px",
                }}
              >
                {votingPeriod
                  ? JSON.stringify(votingPeriod, null, 2)
                  : "No data available"}
              </pre>
            </Box>
          </ModalBody>
          <ModalFooter>
            <Button
              colorScheme="blue"
              mr={3}
              onClick={() => {
                if (votingPeriod) {
                  navigator.clipboard.writeText(
                    JSON.stringify(votingPeriod, null, 2)
                  );
                  toast({
                    title: "Copied",
                    description: "Raw data copied to clipboard",
                    status: "success",
                    duration: 2000,
                    isClosable: true,
                  });
                }
              }}
            >
              Copy to Clipboard
            </Button>
            <Button colorScheme="gray" onClick={onVotingPeriodJsonModalClose}>
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Project JSON Modal */}
      <Modal isOpen={isJsonModalOpen} onClose={onJsonModalClose} size="4xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            Project JSON Data -{" "}
            {selectedProject?.launchpad?.token?.name || "N/A"} (
            {selectedProject?.launchpad?.token?.symbol || "N/A"})
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody fontSize="sm">
            <Box
              p={4}
              bg="gray.50"
              borderRadius="md"
              maxH="70vh"
              overflowY="auto"
            >
              <pre
                style={{
                  whiteSpace: "pre-wrap",
                  wordWrap: "break-word",
                  fontFamily: "monospace",
                  fontSize: "12px",
                }}
              >
                {selectedProject
                  ? JSON.stringify(selectedProject, null, 2)
                  : "No data selected"}
              </pre>
            </Box>
          </ModalBody>
          <ModalFooter>
            <Button
              colorScheme="blue"
              mr={3}
              onClick={() => {
                if (selectedProject) {
                  navigator.clipboard.writeText(
                    JSON.stringify(selectedProject, null, 2)
                  );
                  toast({
                    title: "Copied",
                    description: "Raw data copied to clipboard",
                    status: "success",
                    duration: 2000,
                    isClosable: true,
                  });
                }
              }}
            >
              Copy to Clipboard
            </Button>
            <Button colorScheme="gray" onClick={onJsonModalClose}>
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Project Votes Modal */}
      {id && (
        <ProjectVotesModal
          isOpen={isVotesModalOpen}
          onClose={onVotesModalClose}
          votingPeriodId={id}
          project={selectedProject}
        />
      )}

      {/* Update Campaign Times Modal */}
      <UpdateCampaignTimesModal
        isOpen={isUpdateTimesModalOpen}
        onClose={onUpdateTimesModalClose}
        campaign={votingPeriod}
        onSuccess={async () => {
          // Refresh voting period data after successful update
          const currentId = getVotingPeriodIdFromUrl(
            location.pathname || window.location.href
          );
          if (currentId) {
            try {
              const data = await getVotingPeriodById(currentId);
              setVotingPeriod(data);
            } catch (error: any) {
              toast({
                title: "Error",
                description:
                  error?.message || "Failed to refresh voting period details",
                status: "error",
                duration: 5000,
                isClosable: true,
              });
            }
          }
        }}
      />

      {/* Add Project Modal */}
      <AddProjectModal
        isOpen={isAddProjectModalOpen}
        onClose={onAddProjectModalClose}
        campaign={votingPeriod}
        onSuccess={async () => {
          // Refresh projects list after successful addition
          const currentId = getVotingPeriodIdFromUrl(
            location.pathname || window.location.href
          );
          if (currentId) {
            try {
              const projectsData = await getVotingPeriodProjects(currentId);
              setProjects(projectsData);
            } catch (error: any) {
              toast({
                title: "Error",
                description: error?.message || "Failed to refresh projects",
                status: "error",
                duration: 5000,
                isClosable: true,
              });
            }
          }
        }}
      />

      {/* Pause Campaign Confirmation Dialog */}
      <AlertDialog
        isOpen={isPauseDialogOpen}
        leastDestructiveRef={cancelRef}
        onClose={onPauseDialogClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Pause Campaign
            </AlertDialogHeader>

            <AlertDialogBody>
              Are you sure you want to pause this campaign? The campaign will be
              temporarily stopped and can be resumed later.
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button
                ref={cancelRef}
                onClick={onPauseDialogClose}
                isDisabled={isPausing}
              >
                Cancel
              </Button>
              <Button
                colorScheme="yellow"
                onClick={handlePauseCampaign}
                ml={3}
                isLoading={isPausing}
              >
                Pause
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {/* Resume Campaign Confirmation Dialog */}
      <AlertDialog
        isOpen={isResumeDialogOpen}
        leastDestructiveRef={cancelRef}
        onClose={onResumeDialogClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Resume Campaign
            </AlertDialogHeader>

            <AlertDialogBody>
              Are you sure you want to resume this campaign? The campaign will
              continue from where it was paused.
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button
                ref={cancelRef}
                onClick={onResumeDialogClose}
                isDisabled={isResuming}
              >
                Cancel
              </Button>
              <Button
                colorScheme="green"
                onClick={handleResumeCampaign}
                ml={3}
                isLoading={isResuming}
              >
                Resume
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {/* Set Winner Confirmation Dialog */}
      <AlertDialog
        isOpen={isSetWinnerDialogOpen}
        leastDestructiveRef={cancelRef}
        onClose={onSetWinnerDialogClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Set Winner
            </AlertDialogHeader>

            <AlertDialogBody>
              Are you sure you want to set{" "}
              <strong>
                {selectedProjectForWinner?.launchpad?.token?.name ||
                  selectedProjectForWinner?.launchpad?.token?.symbol ||
                  "this project"}
              </strong>{" "}
              as the winner? This action cannot be undone.
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button
                ref={cancelRef}
                onClick={() => {
                  onSetWinnerDialogClose();
                  setSelectedProjectForWinner(null);
                }}
                isDisabled={isSettingWinner}
              >
                Cancel
              </Button>
              <Button
                colorScheme="purple"
                onClick={handleSetWinner}
                ml={3}
                isLoading={isSettingWinner}
              >
                Set Winner
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {/* Delete Project Confirmation Dialog */}
      <AlertDialog
        isOpen={isDeleteProjectDialogOpen}
        leastDestructiveRef={cancelRef}
        onClose={onDeleteProjectDialogClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold" color="red.500">
              Delete Project
            </AlertDialogHeader>

            <AlertDialogBody>
              Are you sure you want to remove{" "}
              <strong>
                {selectedProjectForDelete?.launchpad?.token?.name ||
                  selectedProjectForDelete?.launchpad?.token?.symbol ||
                  "this project"}
              </strong>{" "}
              from eligible projects? This action cannot be undone.
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button
                ref={cancelRef}
                onClick={() => {
                  onDeleteProjectDialogClose();
                  setSelectedProjectForDelete(null);
                }}
                isDisabled={isDeletingProject}
              >
                Cancel
              </Button>
              <Button
                colorScheme="red"
                onClick={handleDeleteProject}
                ml={3}
                isLoading={isDeletingProject}
              >
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {/* Calculate Eligible Confirmation Dialog */}
      <AlertDialog
        isOpen={isCalculateEligibleDialogOpen}
        leastDestructiveRef={cancelRef}
        onClose={onCalculateEligibleDialogClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Calculate Eligible Projects
            </AlertDialogHeader>

            <AlertDialogBody>
              This action will calculate eligible projects based on the
              campaign's eligibility criteria. It will:
              <br />
              <br />
              • Add new eligible projects that meet the criteria
              <br />
              • Re-add previously removed projects if they are still eligible
              <br />
              <br />
              <strong>
                Note: This action will NOT remove any existing projects.
              </strong>
              <br />
              <br />
              Are you sure you want to proceed?
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button
                ref={cancelRef}
                onClick={onCalculateEligibleDialogClose}
                isDisabled={isCalculatingEligible}
              >
                Cancel
              </Button>
              <Button
                colorScheme="teal"
                onClick={handleCalculateEligible}
                ml={3}
                isLoading={isCalculatingEligible}
              >
                Calculate
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

export default VotingPeriodDetail;
